/**
 * InventoryListPage — 재고관리 (WAREHOUSE DB, MyBatis 기반).
 *
 * 【역할】
 * - 창고별 재고 CRUD — 다중 DB 연결(WAREHOUSE) 샘플
 * - MyBatis 스타일 쿼리 + Oracle 프로시저 호출 혼합
 *
 * 【주요 기능】
 * - 검색 (품목코드/창고/수량 범위/유효기간)
 * - CRUD (편집 모달 + 벌크 저장)
 * - Tab 1: 목록 + 편집
 * - Tab 2: 창고별 요약 (프로시저)
 * - 수량 조정 (프로시저 — 입고/출고 트랜잭션)
 *
 * 【사용 훅】
 * - useFramework ($createDataSet, $transaction, $msg)
 * - useCodes / useLabel
 *
 * 【Backend RPC serviceName】
 * - inventory/searchList  (action=search)   — 목록 조회
 * - inventory/save        (action=save)     — 벌크 저장
 * - inventory/summary     (action=callProc) — 창고별 요약 프로시저
 * - inventory/adjust      (action=callProc) — 수량 조정 프로시저
 *
 * 【DataSet 구조】
 * - ds_list: 재고 목록 (PK: inventoryId, 변경추적 C/U/D)
 * - ds_summary: 창고별 집계 (조회 전용)
 *
 * 【사용처】 router/index.tsx — `{ path: 'inventory' }`
 * 【포팅 원본】 vue-spring-fw/frontend/src/pages/PageInventoryList.vue
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Form,
  Input,
  InputNumber,
  Select,
  Modal,
  DatePicker,
  Button,
  Tabs,
  Space,
  Flex
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { Dayjs } from 'dayjs'
import { useFramework } from '@/framework/useFramework'
import { useCodes } from '@/framework/useCodes'
import { useLabel } from '@/framework/useLabel'
import RowStateTag from '@/components/common/RowStateTag'
import AppPage from '@/components/common/AppPage'
import CrudToolbar from '@/components/common/CrudToolbar'
import DataTable from '@/components/common/DataTable'
import FormSelect from '@/components/common/FormSelect'
import FormInput from '@/components/common/FormInput'
import { fmtDate } from '@/utils/format'

interface InventoryRow {
  /** 재고 PK — Oracle SEQ 채번 */
  inventoryId?: string
  /** 품목코드 — 외부 상품 시스템 키 */
  itemCode?: string
  /** 품목명 — 표시용 */
  itemName?: string
  /** 창고 코드 — 공통코드 WAREHOUSE (WH-A/WH-B/...) */
  warehouseCd?: string
  /** 수량 — 음수 불가, 프로시저 adjust 로 변경 권장 */
  quantity?: number
  /** 단위 — 공통코드 UNIT (EA/KG/...) */
  unit?: string
  /** 최종입고일 — DB 저장 시 'YYYY-MM-DD' 문자열, 폼 편집 중에는 Dayjs */
  lastInboundDate?: string | Dayjs
  /** 비고 */
  note?: string
}

interface SummaryRow {
  /** 창고 코드 (group by 키) */
  warehouseCd?: string
  /** 품목 종류 수 (distinct itemCode 카운트) */
  itemCount?: number
  /** 총 수량 합계 */
  totalQty?: number
  /** 마지막 입고일 (max(lastInboundDate)) */
  lastInbound?: string
}

/**
 * InventoryListPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function InventoryListPage() {
  const { $createDataSet, $transaction, $msg } = useFramework()
  const { getCodeName, getOptions, loadCodes } = useCodes()
  const { t } = useLabel()

  const ds_list = $createDataSet<InventoryRow>([])
  const ds_summary = $createDataSet<SummaryRow>([])

  const [searchForm] = Form.useForm()
  const [editForm] = Form.useForm<InventoryRow>()
  const [editOpen, setEditOpen] = useState(false)
  const [editMode, setEditMode] = useState<'C' | 'U'>('C')
  const [editingIndex, setEditingIndex] = useState<number>(-1)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [loading, setLoading] = useState(false)

  // 수량 조정 Dialog
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [adjustTarget, setAdjustTarget] = useState<InventoryRow | null>(null)
  const [adjustDelta, setAdjustDelta] = useState<number>(0)

  useEffect(() => {
    // ─── 공통코드 lazy load (WAREHOUSE/UNIT) ─
    // 라이프사이클: mount → loadCodes → 그리드 render 시 getCodeName 으로 코드명 해석
    loadCodes(['WAREHOUSE', 'UNIT'])
  }, [loadCodes])

  /** 검색 — 폼 조건으로 ds_list (재고 목록) 조회 + 선택 해제. */
  const fn_search = async () => {
    // ─── 검색 조건 빌드 + 트랜잭션 ─
    // 1) 로딩 표시
    setLoading(true)
    // 2) RPC — outds 로 ds_inventory 페이로드를 ds_list 에 직접 바인딩
    //    MyBatis 경로(camelCase) → 별도 키 정규화 불필요
    const res = await $transaction({
      id: 'select',
      service: 'inventory/searchList',
      action: 'search',
      params: searchForm.getFieldsValue(),
      outds: { ds_inventory: ds_list }
    })
    setLoading(false)
    // 3) 성공 시 행 선택 초기화
    if (res.success) setSelectedRowKeys([])
  }

  /** 검색 초기화 — 폼/list/summary/선택 모두 reset. */
  const fn_reset = () => {
    // ─── 폼/2개 DS/선택 모두 초기화 ─
    searchForm.resetFields()
    ds_list.clear()
    ds_summary.clear()
    setSelectedRowKeys([])
  }

  /** 신규 — 편집 모달을 C 모드로 열고 기본값(WH-A/0/EA) 주입. */
  const fn_add = () => {
    // ─── 편집 모달 C 모드 진입 ─
    setEditMode('C')
    setEditingIndex(-1)
    editForm.resetFields()
    // 합리적 기본값 주입 (가장 자주 쓰는 값으로)
    editForm.setFieldsValue({
      warehouseCd: 'WH-A',
      quantity: 0,
      unit: 'EA'
    })
    setEditOpen(true)
  }

  /** 수정 — 단일 선택 행을 편집 모달 U 모드로 로딩 (날짜 string→Dayjs 변환). */
  const fn_edit = () => {
    // ─── 단일 선택 검증 + 편집 모달 U 모드 ─
    // 1) 선택행 1개 검증
    if (selectedRowKeys.length !== 1) {
      $msg.byId('MSG_SELECT_EDIT_ROW')
      return
    }
    const idx = visibleRows.findIndex((r) => r._rowId === selectedRowKeys[0])
    if (idx < 0) return
    setEditMode('U')
    setEditingIndex(idx)
    // 2) DB string('YYYY-MM-DD') → DatePicker 가 받는 Dayjs 로 변환
    const data = { ...visibleRows[idx].data }
    if (data.lastInboundDate && typeof data.lastInboundDate === 'string') {
      data.lastInboundDate = dayjs(data.lastInboundDate)
    }
    editForm.setFieldsValue(data)
    setEditOpen(true)
  }

  /** 편집 모달 OK — Dayjs→string 변환 후 ds 반영 (C/U 분기). */
  const fn_editSubmit = async () => {
    // ─── 폼 검증 + Dayjs 직렬화 + ds 반영 ─
    // 1) AntD Form validate
    const values = await editForm.validateFields()
    // 2) Dayjs → 'YYYY-MM-DD' (DB 저장 포맷)
    const patch: InventoryRow = {
      ...values,
      lastInboundDate:
        values.lastInboundDate && dayjs.isDayjs(values.lastInboundDate)
          ? (values.lastInboundDate as Dayjs).format('YYYY-MM-DD')
          : (values.lastInboundDate as string | undefined)
    }
    if (editMode === 'C') {
      // 3-C) addRow → _state='C' 마킹
      ds_list.addRow(patch)
    } else {
      // 3-U) visible→actual 인덱스 매핑 후 updateRow
      const targetId = visibleRows[editingIndex]?._rowId
      const actualIdx = ds_list.rows.findIndex((r) => r._rowId === targetId)
      if (actualIdx >= 0) ds_list.updateRow(actualIdx, patch)
    }
    setEditOpen(false)
  }

  /** 삭제 — 선택 행을 일괄 deleteRow (확인 다이얼로그 후). */
  const fn_delete = () => {
    // ─── 선택 검증 + confirm + 일괄 삭제 ─
    if (!selectedRowKeys.length) {
      $msg.byId('MSG_SELECT_DELETE_ROW')
      return
    }
    $msg.confirmById('MSG_CONFIRM_DELETE', () => {
      // C/U 행은 'D' 마킹, N 행은 즉시 제거
      for (const key of selectedRowKeys) {
        const idx = ds_list.rows.findIndex((r) => r._rowId === key)
        if (idx >= 0) ds_list.deleteRow(idx)
      }
      setSelectedRowKeys([])
    })
  }

  /** 저장 — 변경분(ds_list)을 inventory/save 로 일괄 전송 후 재조회. */
  const fn_save = async () => {
    // ─── dirty 체크 + 벌크 저장 ─
    if (!ds_list.isDirty) {
      $msg.byId('MSG_NO_CHANGES')
      return
    }
    // inds: 변경분만 직렬화 / outds: 채번 PK 회신 받아 ds_list 갱신
    const res = await $transaction({
      id: 'save',
      service: 'inventory/save',
      action: 'save',
      inds: { ds_inventory: ds_list },
      outds: { ds_inventory: ds_list }
    })
    if (res.success) {
      $msg.byId('MSG_SAVE_SUCCESS')
      fn_search() // 서버 trigger/SEQ 동기화
    }
  }

  /** 취소 — 변경 폐기 후 재조회. */
  const fn_cancel = () => fn_search()

  /** 창고요약 — Oracle 프로시저 호출로 창고별 집계 ds_summary 채움. */
  const fn_summary = async () => {
    // ─── 프로시저 호출 (창고별 group by 집계) ─
    // outds: ds_summary 페이로드를 ds_summary 에 직접 바인딩
    await $transaction({
      id: 'summary',
      service: 'inventory/summary',
      action: 'search',
      params: searchForm.getFieldsValue(),
      outds: { ds_summary: ds_summary }
    })
  }

  /** 수량조정 모달 open — 단일 선택 행 정보로 dialog state 초기화. */
  const fn_openAdjust = () => {
    // ─── 단일 선택 검증 + 조정 dialog 진입 ─
    if (selectedRowKeys.length !== 1) {
      $msg.byId('MSG_SELECT_ROW')
      return
    }
    const row = visibleRows.find((r) => r._rowId === selectedRowKeys[0])
    if (!row) return
    // 대상 행 + 증감값(0) 으로 dialog 초기화
    setAdjustTarget(row.data)
    setAdjustDelta(0)
    setAdjustOpen(true)
  }

  /** 수량조정 실행 — Oracle 프로시저로 입출고 트랜잭션 (resultCode 분기). */
  const fn_adjust = async () => {
    // ─── 프로시저 호출 + resultCode 분기 ─
    // 1) early-return — 대상 없거나 증감 0 이면 무시
    if (!adjustTarget || adjustDelta === 0) return
    // 2) 프로시저 호출 (delta>0=입고, delta<0=출고, 재고부족 시 -2)
    const res = await $transaction({
      id: 'adjust',
      service: 'inventory/adjust',
      action: 'save',
      params: { inventoryId: adjustTarget.inventoryId, delta: adjustDelta }
    })
    if (res.success) {
      // 3) ds_adjustResult 의 resultCode 분기 (0=성공/-2=재고부족/그외=대상 없음)
      const result = (res.data?.ds_adjustResult ?? res.data) as
        | { resultCode?: number; newQuantity?: number }
        | undefined
      const rc = result?.resultCode
      if (rc === 0) {
        $msg.success(`수량 조정 완료: ${result?.newQuantity}`)
        setAdjustOpen(false)
        fn_search() // 변경된 수량 동기화
      } else if (rc === -2) {
        $msg.byId('MSG_INVENTORY_SHORTAGE')
      } else {
        $msg.error('재고를 찾을 수 없습니다.')
      }
    }
  }

  const visibleRows = ds_list.getVisibleRows()

  const columns: ColumnsType<(typeof visibleRows)[number]> = useMemo(
    () => [
      { title: 'ID', dataIndex: ['data', 'inventoryId'], width: 80 },
      { title: t('COL_ITEM_CODE', '품목코드'), dataIndex: ['data', 'itemCode'], width: 140 },
      { title: t('COL_ITEM_NAME', '품목명'), dataIndex: ['data', 'itemName'], width: 200 },
      {
        title: t('COL_WAREHOUSE', '창고'),
        dataIndex: ['data', 'warehouseCd'],
        width: 120,
        render: (v: string) => getCodeName('WAREHOUSE', v)
      },
      {
        title: t('COL_QTY', '수량'),
        dataIndex: ['data', 'quantity'],
        width: 110,
        align: 'right',
        render: (v?: number) => v?.toLocaleString() ?? ''
      },
      {
        title: t('COL_UNIT', '단위'),
        dataIndex: ['data', 'unit'],
        width: 90,
        render: (v: string) => getCodeName('UNIT', v)
      },
      {
        title: t('COL_LAST_INBOUND', '최종입고일'),
        dataIndex: ['data', 'lastInboundDate'],
        width: 140,
        render: (v) => fmtDate(v)
      },
      { title: t('COL_NOTE', '비고'), dataIndex: ['data', 'note'] },
      {
        title: t('COL_STATUS', '상태'),
        dataIndex: '_state',
        width: 70,
        render: (s: string) => <RowStateTag state={s} updateColor="gold" />
      }
    ],
    [getCodeName, t]
  )

  const summaryColumns: ColumnsType<SummaryRow> = [
    {
      title: t('COL_WAREHOUSE', '창고'),
      dataIndex: 'warehouseCd',
      width: 120,
      render: (v: string) => getCodeName('WAREHOUSE', v)
    },
    {
      title: t('LBL_ITEM_COUNT', '품목수'),
      dataIndex: 'itemCount',
      width: 120,
      align: 'right',
      render: (v?: number) => v?.toLocaleString() ?? ''
    },
    {
      title: t('LBL_TOTAL_QTY', '총수량'),
      dataIndex: 'totalQty',
      width: 140,
      align: 'right',
      render: (v?: number) => v?.toLocaleString() ?? ''
    },
    {
      title: t('COL_LAST_INBOUND', '최종입고일'),
      dataIndex: 'lastInbound',
      width: 140,
      render: (v) => fmtDate(v)
    }
  ]

  useEffect(() => {
    // ─── 초기 로드 (mount 1회) ─
    // 라이프사이클: mount → fn_search → ds_list 채움 → 그리드 렌더
    fn_search()
  }, []) // deps 비움 — 1회만 실행

  return (
    <>
      <AppPage
        search={{
          form: searchForm,
          onSearch: fn_search,
          onReset: fn_reset,
          loading,
          content: (
            <>
              <Form.Item name="keyword" label={t('LBL_KEYWORD', '키워드')}>
                <FormInput
                  placeholder={t('PH_PRODUCT_NAME', '품목코드/품목명')}
                  style={{ width: 200 }}
                />
              </Form.Item>
              <Form.Item name="warehouseCd" label={t('COL_WAREHOUSE', '창고')}>
                <FormSelect
                  options={getOptions('WAREHOUSE')}
                  style={{ minWidth: 160 }}
                  placeholder={t('PH_ALL', '전체')}
                />
              </Form.Item>
            </>
          )
        }}
        toolbar={
          <Flex
            justify="space-between"
            align="center"
            gap={8}
            style={{ width: '100%', flexWrap: 'nowrap', overflowX: 'auto' }}
          >
            <Space size="small" wrap={false} style={{ flexShrink: 0 }}>
              <Button onClick={fn_summary}>{t('LBL_WAREHOUSE_SUMMARY', '창고요약')}</Button>
              <Button onClick={fn_openAdjust} disabled={selectedRowKeys.length !== 1}>
                수량조정
              </Button>
            </Space>
            <CrudToolbar
              onSearch={fn_search}
              onAdd={fn_add}
              onEdit={fn_edit}
              onDelete={fn_delete}
              onSave={fn_save}
              onCancel={fn_cancel}
              loading={loading}
              dirty={ds_list.isDirty}
              style={{ width: 'auto', flexShrink: 0 }}
            />
          </Flex>
        }
      >
        <Tabs
          items={[
            {
              key: 'list',
              label: '재고목록 (MyBatis)',
              children: (
                <DataTable
                  rowKey={(r) => r._rowId}
                  columns={columns}
                  dataSource={visibleRows}
                  loading={loading}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys)
                  }}
                />
              )
            },
            {
              key: 'summary',
              label: '창고요약 (Procedure)',
              children: (
                <DataTable
                  rowKey={(r) => r.warehouseCd ?? ''}
                  columns={summaryColumns}
                  dataSource={ds_summary.getVisibleRows().map((r) => r.data)}
                />
              )
            }
          ]}
        />
      </AppPage>

      <Modal
        open={editOpen}
        title={editMode === 'C' ? '재고 추가' : '재고 수정'}
        onOk={fn_editSubmit}
        onCancel={() => setEditOpen(false)}
        okText="확인"
        cancelText="취소"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          {editMode === 'U' && (
            <Form.Item name="inventoryId" label="ID">
              <Input disabled />
            </Form.Item>
          )}
          <Form.Item
            name="itemCode"
            label={t('COL_ITEM_CODE', '품목코드')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="itemName"
            label={t('COL_ITEM_NAME', '품목명')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="warehouseCd"
            label={t('COL_WAREHOUSE', '창고')}
            rules={[{ required: true }]}
          >
            <Select options={getOptions('WAREHOUSE')} />
          </Form.Item>
          <Form.Item name="quantity" label={t('COL_QTY', '수량')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="unit" label={t('COL_UNIT', '단위')}>
            <Select options={getOptions('UNIT')} />
          </Form.Item>
          <Form.Item name="lastInboundDate" label={t('COL_LAST_INBOUND', '최종입고일')}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="note" label={t('COL_NOTE', '비고')}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={adjustOpen}
        title={t('TTL_QTY_ADJUST', '수량조정')}
        onOk={fn_adjust}
        onCancel={() => setAdjustOpen(false)}
        okText={t('BTN_OK', '확인')}
        cancelText={t('BTN_CANCEL', '취소')}
      >
        {adjustTarget && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <strong>{adjustTarget.itemName}</strong> ({adjustTarget.itemCode})
            </div>
            <div>현재수량: {adjustTarget.quantity?.toLocaleString()}</div>
            <div>
              <span style={{ marginRight: 8 }}>증감:</span>
              <InputNumber
                value={adjustDelta}
                onChange={(v) => setAdjustDelta(Number(v ?? 0))}
                style={{ width: 160 }}
              />
            </div>
            <small style={{ color: '#999' }}>양수=입고(+), 음수=출고(-)</small>
          </div>
        )}
      </Modal>
    </>
  )
}
